import { useChain, useSpringRef } from "@react-spring/web";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import useGtmPageView from "@/app/common-hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/app/common-hooks/page-tracking/use-custom-page-tracking";
import useResetStateEdit from "@/app/common-hooks/use-reset-state-question/use-reset-state-question.hook";
import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import {
  CruiseEdit,
  QuoteEdit,
  DatesEdit,
  DestinationEdit,
  MedicalEdit,
  TravellerEdit,
  ContactEdit,
  CoverEdit,
  OrganizerEdit,
} from "@/config/app-constant";
import { APPLICATION } from "@/constants";
import setCookieForHeader from "@/hooks/api/cookie/get-cookie";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { GetQuoteFilter } from "@/store/slices/quote/API/getQuote";
import { getQuoteData } from "@/store/slices/quote/quote.action";
import { clearQuote } from "@/store/slices/quote/quote.slice";
import { RootState, store } from "@/store/store";
import { storageAvailable } from "@/utils/common.utils";

export interface ScrollRefItem {
  ref: React.RefObject<HTMLElement | null>;
  isToggleWrapper?: boolean;
}

export interface ScrollOptions {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
}

function useQuestionLayoutHook({
  scrollRefs,
}: {
  scrollRefs: ScrollRefItem[];
}) {
  const dispatch = useDispatch<typeof store.dispatch>();
  const formAnimationRef = useSpringRef();
  const footerAnimationRef = useSpringRef();
  const descriptionContentSpringRef = useSpringRef();
  const { t } = useWizardTranslation();

  const searchParams = useSearchParams();
  const travellerEditPage = searchParams.get(TravellerEdit);
  const cruiseEditPage = searchParams.get(CruiseEdit);
  const quoteEditPage = searchParams.get(QuoteEdit);
  const datesEditPage = searchParams.get(DatesEdit);
  const destinationEditPage = searchParams.get(DestinationEdit);
  const medicalEditPage = searchParams.get(MedicalEdit);
  const contactEditPage = searchParams.get(ContactEdit);
  const coverEditPage = searchParams.get(CoverEdit);
  const organizerEditPage = searchParams.get(OrganizerEdit);

  const { resetQuestionData } = useResetStateEdit();

  const [isLoading, setIsLoading] = useState(true);
  const [isCancelEditModalOpen, setIsCancelEditModalOpen] =
    useState<boolean>(false);
  const [dynamicFooter, setDynamicFooter] = useState<boolean>(false);
  const { loading, referenceId } = useSelector(
    (state: RootState) => state.quote
  );

  const descriptionRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 904px)");

  const router = useRouter();
  const pathname = usePathname();
  const quoteRef = searchParams.get("quoteId");
  const affiliate = searchParams.get("affiliate");
  const handleCancelEditModalConfirmBtnClick = () => {
    resetQuestionData();

    // Check if user came from quote-review edit flow
    const editSection = getQuoteReviewSummaryEditSection();

    if (editSection) {
      // Navigate to quote-review page to expand the relevant section
      router.push("/quote-review");
      // Note: sessionStorage "quoteReviewEditSection" will be read by quote-review page
    } else {
      // Original behavior: navigate to quote page with editQuote flag
      let path = t("quote_path", "quote");
      router.push(`/${path}/?editQuote=true`);
    }
  };

  const editPage =
    !!travellerEditPage ||
    !!cruiseEditPage ||
    !!quoteEditPage ||
    !!datesEditPage ||
    !!destinationEditPage ||
    !!medicalEditPage ||
    !!contactEditPage ||
    !!coverEditPage ||
    !!organizerEditPage;

  // Scroll to fallback ref (footer on desktop, description on mobile)
  const scrollToFallback = useCallback(
    (options: ScrollOptions = {}, delay = 500) => {
      const fallbackRef = isDesktop ? footerRef : descriptionRef;
      const defaultOptions: ScrollOptions = {
        behavior: "smooth",
        block: "end",
        ...options,
      };

      if (delay > 0) {
        setTimeout(() => {
          fallbackRef?.current?.scrollIntoView(defaultOptions);
        }, delay);
      } else {
        fallbackRef?.current?.scrollIntoView(defaultOptions);
      }
    },
    [isDesktop, footerRef, descriptionRef]
  );

  const scrollToNext = useCallback(
    (refValue: ScrollRefItem, indexNo: number, shouldFocus = false) => {
      if (refValue.isToggleWrapper) {
        const isActiveButtonAvailable = refValue.ref.current?.querySelector(
          ".componnt_lib_toggle_btn_active"
        );
        if (!isActiveButtonAvailable) {
          return;
        }
      }

      const nextRef = scrollRefs[indexNo + 1];

      if (nextRef?.ref?.current) {
        nextRef.ref.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        if (shouldFocus) {
          nextRef.ref.current.focus?.();
        }
      } else {
        scrollToFallback({}, 0);
      }
    },
    [scrollRefs, scrollToFallback]
  );

  useEffect(() => {
    // Store cleanup functions to prevent memory leaks
    const cleanupFunctions: (() => void)[] = [];

    scrollRefs.forEach((refValue, index) => {
      const el = refValue.ref.current;
      if (!el) return;

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
          scrollToNext(refValue, index, true); // Focus on Enter
        }
      };

      const handleScrollToNext = () => {
        scrollToNext(refValue, index); // No focus on click
      };

      const onClick = () => {
        setTimeout(handleScrollToNext, 500);
      };

      el.addEventListener("keydown", onKeyDown);
      el.addEventListener("click", onClick);

      cleanupFunctions.push(() => {
        el.removeEventListener("keydown", onKeyDown);
        el.removeEventListener("click", onClick);
      });
    });

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRefs.length, scrollToNext]);

  useEffect(() => {
    if (isLoading && !loading) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (typeof window !== "undefined" && window) {
      if (storageAvailable() && !sessionStorage.getItem("footer")) {
        setDynamicFooter(true);
      }
    }
  }, []);

  useChain(
    [formAnimationRef, footerAnimationRef, descriptionContentSpringRef],
    [0, 0.5, 1]
  );
  const pageName = pathname;

  const { pageReferrer, pageUrl, pageTitle } = useCustomPageTracking(pageName);

  let getQuoteFilter: GetQuoteFilter = {
    affiliate: null,
    quoteId: null,
  };

  if (!affiliate && !quoteRef) {
    getQuoteFilter.affiliate = "quoteInit";
  } else {
    getQuoteFilter = {
      affiliate,
      quoteId: quoteRef,
    };
  }

  const fetchQuoteDataComponent = async () => {
    if (getQuoteFilter.affiliate && APPLICATION !== "renewal") {
      if (pathname === "/") {
        await setCookieForHeader();
      }

      if (
        ((storageAvailable() && sessionStorage.getItem("pageReload")) ||
          !sessionStorage.getItem("initialLoad")) &&
        !referenceId
      ) {
        sessionStorage.setItem("initialLoad", "true");
        sessionStorage.removeItem("pageReload");
        dispatch(clearQuote());
        await dispatch(getQuoteData({ getQuoteFilter, dispatch, router }));
      }
    }
  };

  useEffect(() => {
    fetchQuoteDataComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });
  return {
    formAnimationRef,
    footerAnimationRef,
    descriptionContentSpringRef,
    descriptionRef,
    editPage: !!editPage,
    footerRef,
    isLoading,
    isCancelEditModalOpen,
    dynamicFooter,
    setIsCancelEditModalOpen,
    handleCancelEditModalConfirmBtnClick,
    scrollToFallback,
  };
}

export default useQuestionLayoutHook;
